import { useEffect, useRef } from 'react';
import { useIsMobile } from './useIsMobile';
import { isTauri } from '../utils/tauri';

// 안드로이드 뒤로가기(하드웨어 버튼·제스처)로 떠 있는 오버레이를 닫는다.
//
// history(pushState)가 아니라 CloseWatcher(Chrome 120+)를 쓰는 이유: Chrome은 사용자 입력 없이
// 쌓인 history 항목을 뒤로가기 버튼에서 건너뛴다(History Manipulation Intervention). 알림으로 막 뜬
// 페이지처럼 입력이 0인 상태에선 pushState로 만든 '닫기용 항목'이 통째로 무시되고 앱이 닫힌다.
// CloseWatcher는 뒤로가기를 history 이동보다 먼저 받으며 입력 없이도 1개는 만들 수 있다.
//
// 닫힘 순서 = 생성 순서의 역순(서랍 위 사진 뷰어 → 뷰어가 먼저). 입력 없이 연달아 만든 watcher는
// 브라우저가 한 그룹으로 묶어 뒤로가기 한 번에 함께 닫는다(악용 방지 규칙 — 피할 수 없다).
// 미지원 브라우저(iOS·구버전)에선 아무것도 하지 않는다 = 기존 동작 그대로(fail-open).

interface CloseWatcherLike {
  onclose: ((e: Event) => void) | null;
  destroy(): void;
}

/** CloseWatcher 1개를 만든다. 미지원이거나 생성에 실패하면 null. */
export function createCloseWatcher(onClose: () => void): CloseWatcherLike | null {
  const Ctor = (window as unknown as { CloseWatcher?: new () => CloseWatcherLike }).CloseWatcher;
  if (typeof Ctor !== 'function') return null;
  try {
    const watcher = new Ctor();
    watcher.onclose = () => onClose();
    return watcher;
  } catch {
    return null;
  }
}

/**
 * `active`인 동안 뒤로가기 한 번이 `onClose`를 부르게 한다. **모바일 웹에서만** 켠다 —
 * 데스크톱·Tauri에선 Esc 키도 닫기 요청이라, 켜면 각 오버레이의 기존 ESC 처리
 * (Modal의 modalStack·ContextMenu keydown)와 겹쳐 한 번에 두 번 닫힌다.
 */
export function useCloseWatcher(active: boolean, onClose: () => void) {
  const isMobile = useIsMobile();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!active || !isMobile || isTauri()) return;
    const watcher = createCloseWatcher(() => onCloseRef.current());
    return () => watcher?.destroy();
  }, [active, isMobile]);
}
